import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CircleNotch, CaretUp, CaretDown, Shapes } from '@phosphor-icons/react';
import WindowNarrative from '../components/WindowNarrative';
import { ExportButton } from '../components/ExportCSV';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECADE_COLORS = {
  '1 to 10': 'bg-sky-100 text-sky-800',
  '11 to 20': 'bg-emerald-100 text-emerald-800',
  '21 to 30': 'bg-amber-100 text-amber-800',
  '31 to 40': 'bg-orange-100 text-orange-800',
  '41 to 50': 'bg-rose-100 text-rose-800',
};

const DecadeChip = ({ label }) => (
  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${DECADE_COLORS[label] || 'bg-gray-100 text-gray-700'}`}>
    {label.replace(' to ', '-')}
  </span>
);

const overdueTier = (pct) => {
  if (pct == null) return null;
  if (pct >= 200) return { label: 'OVERDUE', cls: 'bg-red-600 text-white' };
  if (pct >= 100) return { label: 'DUE', cls: 'bg-amber-500 text-white' };
  if (pct >= 75) return { label: 'APPROACHING', cls: 'bg-sky-500 text-white' };
  return null;
};

/* ================================================================
 * Narrative — Introduction / Observations / Key Findings
 * ================================================================ */
function buildIntro(data) {
  const total = data?.total_draws;
  const latest = data?.latest_draw;
  const latestDate = data?.latest_date;
  return {
    testid: 'types-introduction',
    body: (
      <>
        <strong>W67: Types</strong> classifies every draw by its <strong>decade-bucket shape</strong>. Each of the 5 sorted main balls (1–50) falls into one of 5 decades — 1–10, 11–20, 21–30, 31–40, 41–50. Since the balls are always sorted ascending, their 5 decades form a non-decreasing sequence, and there are exactly <strong>126</strong> possible sequences (combinatorially, C(9,5)=126) — hence &quot;126 Types&quot;.
      </>
    ),
    bullets: [
      <><strong>Example</strong>: a draw of 3, 17, 22, 38, 45 → decades 1,2,3,4,5 → the &quot;one ball per decade&quot; shape — the most balanced shape, and historically the single most common one, so it is ranked <strong>Type 1</strong>.</>,
      <><strong>Numbering</strong>: Types are ranked purely by historical frequency (Came Till 2025), descending — <strong>Type 1</strong> is the most common shape ever, <strong>Type 126</strong> is the least common (or never occurred). This is not a fixed combinatorial listing — the ranking re-settles only if a draw changes the relative frequency order between types, which is rare.</>,
      <>
        <strong>Fully live</strong>: recomputed from all {total ? total.toLocaleString() : '—'} draws on every page load{latest ? <>, currently through draw <strong>#{latest}</strong>{latestDate ? ` (${latestDate})` : ''}</> : ''} — no cache, no snapshot. Adding a new draw updates every row immediately.
      </>,
      <><strong>Columns</strong>: Came till 2025 / Avg per Yr / % (historical baseline) · In 2026 / % (this year so far) · Diff % (2026 minus historical — over/under-represented this year) · Maximum Gap / Current Gap (in draws, biggest-ever drought vs the ongoing one) · Avg Bounce Back (typical gap between occurrences) · Overdue % (Current Gap ÷ Avg Bounce Back — ≥200% OVERDUE, ≥100% DUE, ≥75% APPROACHING, same convention used across W13/W37).</>,
    ],
  };
}

function buildObservations(rows) {
  if (!rows?.length) return null;
  const mostCommon = [...rows].sort((a, z) => z.times_all_time - a.times_all_time)[0];
  const neverOccurred = rows.filter((r) => r.times_all_time === 0);
  const recordDrought = rows.filter((r) => r.times_all_time > 0 && r.current_gap >= r.maximum_gap);
  const biggestUp = [...rows].sort((a, z) => z.diff_pct - a.diff_pct)[0];
  const biggestDown = [...rows].sort((a, z) => a.diff_pct - z.diff_pct)[0];

  return {
    testid: 'types-observations',
    body: 'Auto-generated summary of the current 126-type landscape.',
    bullets: [
      {
        content: <><strong>Most common shape ever</strong>: Type {mostCommon.type_number} ({mostCommon.ball_ranges.map((r) => r.replace(' to ', '-')).join(' · ')}) — {mostCommon.times_all_time}x all-time ({mostCommon.pct_till_2025}% of draws through 2025).</>,
      },
      {
        content: <><strong>{neverOccurred.length} of 126 types</strong> have never occurred in the dataset at all — ranked lowest (closest to Type 126), mostly the extreme &quot;all-in-one-decade&quot; shapes.</>,
      },
      recordDrought.length > 0 && {
        content: <><strong>{recordDrought.length} type{recordDrought.length > 1 ? 's' : ''} currently in an all-time-record drought</strong> — current gap has reached or exceeded the longest gap ever seen for that shape{recordDrought[0] ? <> (e.g. Type {recordDrought[0].type_number}: {recordDrought[0].ball_ranges.map((r) => r.replace(' to ', '-')).join(' · ')}, {recordDrought[0].current_gap} draws and counting)</> : ''}.</>,
      },
      {
        content: <><strong>Trending up in 2026</strong>: Type {biggestUp.type_number} ({biggestUp.ball_ranges.map((r) => r.replace(' to ', '-')).join(' · ')}) is {biggestUp.diff_pct > 0 ? '+' : ''}{biggestUp.diff_pct}% vs its historical baseline. <strong>Trending down</strong>: Type {biggestDown.type_number} at {biggestDown.diff_pct}%.</>,
      },
    ].filter(Boolean),
    footer: 'Diff % compares this year\'s share of draws for a type against its all-time historical share — a large swing either way over a small 2026 sample can still be noise.',
  };
}

function buildKeyFindings(rows) {
  if (!rows?.length) return null;
  const topDue = [...rows]
    .filter((r) => r.overdue_pct != null)
    .sort((a, z) => z.overdue_pct - a.overdue_pct)
    .slice(0, 5);

  return {
    testid: 'types-key-findings',
    title: 'Key Findings — applicable to next draw',
    body: 'Types ranked by Overdue % (Current Gap ÷ Avg Bounce Back) — the same OVERDUE/DUE/APPROACHING convention used across the app\'s other gap-based windows.',
    bullets: topDue.map((r) => {
      const tier = r.overdue_pct >= 200 ? 'OVERDUE' : r.overdue_pct >= 100 ? 'DUE' : r.overdue_pct >= 75 ? 'APPROACHING' : null;
      return {
        content: <>
          <strong>Type {r.type_number}</strong> ({r.ball_ranges.map((x) => x.replace(' to ', '-')).join(' · ')}) → current gap <strong>{r.current_gap}</strong> draws vs avg bounce back <strong>{r.avg_bounce_back}</strong> ({r.overdue_pct}%{tier ? <> — <span className="font-black">{tier}</span></> : ''}), {r.times_all_time}x all-time.
        </>,
      };
    }),
    footer: 'A long gap does not guarantee an imminent hit — this list is context, not a forecast. Cross-check with other windows (e.g. W13 Gap Analysis) before treating any single shape as a strong signal.',
  };
}

/* ================================================================
 * Types table — sortable, 126 rows
 * ================================================================ */
const COLUMNS = [
  { key: 'type_number', label: 'Type' },
  { key: 'pattern', label: 'Decade Pattern', sortable: false },
  { key: 'times_all_time', label: 'All-Time' },
  { key: 'came_till_2025', label: 'Till 2025' },
  { key: 'average_per_year', label: 'Avg/Yr' },
  { key: 'pct_till_2025', label: '%' },
  { key: 'in_2026', label: 'In 2026' },
  { key: 'pct_2026', label: '%' },
  { key: 'diff_pct', label: 'Diff %' },
  { key: 'maximum_gap', label: 'Max Gap' },
  { key: 'current_gap', label: 'Current Gap' },
  { key: 'avg_bounce_back', label: 'Avg Bounce Back' },
  { key: 'overdue_pct', label: 'Overdue %' },
];

function TypesTable({ rows }) {
  const [sortConfig, setSortConfig] = useState({ key: 'type_number', dir: 'asc' });
  const handleSort = (key) => setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ k }) => sortConfig.key !== k ? null : sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aV = a[sortConfig.key] ?? 0, bV = b[sortConfig.key] ?? 0;
      const cmp = aV - bV;
      return sortConfig.dir === 'desc' ? -cmp : cmp;
    });
  }, [rows, sortConfig]);

  const exportData = rows.map((r) => ({
    Type: r.type_number,
    Pattern: r.ball_ranges.join(' | '),
    'All-Time': r.times_all_time,
    'Came Till 2025': r.came_till_2025,
    'Avg Per Year': r.average_per_year,
    '% Till 2025': r.pct_till_2025,
    'In 2026': r.in_2026,
    '% 2026': r.pct_2026,
    'Diff %': r.diff_pct,
    'Maximum Gap': r.maximum_gap,
    'Current Gap': r.current_gap,
    'Avg Bounce Back': r.avg_bounce_back ?? '',
    'Overdue %': r.overdue_pct ?? '',
  }));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="types-table-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-wrap gap-2">
        <h3 className="font-bold text-gray-900">All 126 Types</h3>
        <ExportButton data={exportData} filename="window_types_126" />
      </div>
      <div className="overflow-x-auto max-h-[75vh]">
        <table className="w-full text-xs" data-testid="types-table">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`px-2 py-2 text-[10px] uppercase text-gray-600 font-bold whitespace-nowrap ${c.sortable === false ? '' : 'cursor-pointer hover:bg-gray-100'}`}
                  onClick={c.sortable === false ? undefined : () => handleSort(c.key)}
                  data-testid={`types-col-${c.key}`}
                >
                  {c.label} {c.sortable === false ? null : <SortIcon k={c.key} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const isRecordDrought = r.times_all_time > 0 && r.current_gap >= r.maximum_gap;
              return (
                <tr
                  key={r.type_number}
                  className={`border-b border-gray-50 ${isRecordDrought ? 'bg-rose-50/70' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                  data-testid={`types-row-${r.type_number}`}
                >
                  <td className="px-2 py-1.5 text-center font-mono font-bold text-gray-800">{r.type_number}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-0.5 flex-wrap">
                      {r.ball_ranges.map((rg, idx) => <DecadeChip key={idx} label={rg} />)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.times_all_time}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.came_till_2025}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.average_per_year}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.pct_till_2025}%</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.in_2026}</td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.pct_2026}%</td>
                  <td className={`px-2 py-1.5 text-center font-mono font-bold ${r.diff_pct > 0 ? 'text-emerald-600' : r.diff_pct < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                    {r.diff_pct > 0 ? '+' : ''}{r.diff_pct}%
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.maximum_gap}</td>
                  <td className="px-2 py-1.5 text-center font-mono">
                    <span className={isRecordDrought ? 'px-1.5 py-0.5 rounded bg-rose-600 text-white font-bold' : ''}>{r.current_gap}</span>
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">{r.avg_bounce_back ?? '—'}</td>
                  <td className="px-2 py-1.5 text-center font-mono" data-testid={`types-overdue-pct-${r.type_number}`}>
                    {r.overdue_pct != null ? (
                      <div className="flex items-center justify-center gap-1">
                        <span>{r.overdue_pct}%</span>
                        {overdueTier(r.overdue_pct) && (
                          <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${overdueTier(r.overdue_pct).cls}`} data-testid={`types-overdue-tier-${r.type_number}`}>
                            {overdueTier(r.overdue_pct).label}
                          </span>
                        )}
                      </div>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2 text-[10px] text-gray-500 italic border-t border-gray-100">
        Rows highlighted in rose have a current gap at or beyond their all-time-longest drought.
      </p>
    </div>
  );
}

const BALL_LABELS = ['Ball 1', 'Ball 2', 'Ball 3', 'Ball 4', 'Ball 5'];
const DECADE_LABEL_BY_NUM = { 1: '1 to 10', 2: '11 to 20', 3: '21 to 30', 4: '31 to 40', 5: '41 to 50' };

function PerBallOverdueDecades({ rows }) {
  const overdueRows = rows.filter((r) => r.times_all_time > 0 && r.current_gap >= r.maximum_gap);

  const perBallRanked = useMemo(() => {
    return [0, 1, 2, 3, 4].map((ballIdx) => {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      overdueRows.forEach((r) => { counts[r.signature[ballIdx]] += 1; });
      return Object.entries(counts)
        .map(([decade, count]) => ({ decade: Number(decade), label: DECADE_LABEL_BY_NUM[decade], count }))
        .sort((a, z) => z.count - a.count);
    });
  }, [overdueRows]);

  const byDecade = useMemo(() => {
    const matrix = { 1: [0, 0, 0, 0, 0], 2: [0, 0, 0, 0, 0], 3: [0, 0, 0, 0, 0], 4: [0, 0, 0, 0, 0], 5: [0, 0, 0, 0, 0] };
    overdueRows.forEach((r) => {
      r.signature.forEach((decade, ballIdx) => { matrix[decade][ballIdx] += 1; });
    });
    return [1, 2, 3, 4, 5].map((decade) => {
      const counts = matrix[decade];
      const total = counts.reduce((a, b) => a + b, 0);
      return { decade, label: DECADE_LABEL_BY_NUM[decade], counts, total };
    });
  }, [overdueRows]);

  if (overdueRows.length === 0) return null;

  const maxTotal = Math.max(...byDecade.map((d) => d.total));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="overdue-ball-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-bold text-gray-900">Per-Ball Overdue Decade Totals</h3>
        <span className="text-xs text-gray-500">
          Based on <strong>{overdueRows.length}</strong> type{overdueRows.length !== 1 ? 's' : ''} currently in an all-time-record drought (Current Gap ≥ Maximum Gap)
        </span>
      </div>

      {/* Section A: ranked breakdown per ball (original view) */}
      <h4 className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">By Ball (ranked)</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {perBallRanked.map((decadeCounts, ballIdx) => (
          <div key={ballIdx} className="border border-gray-100 rounded-lg p-3 bg-gray-50/60" data-testid={`overdue-ball-${ballIdx + 1}`}>
            <h4 className="text-xs font-black text-gray-700 uppercase tracking-wide mb-2">{BALL_LABELS[ballIdx]}</h4>
            <ul className="space-y-1.5">
              {decadeCounts.map(({ decade, label, count }) => (
                <li
                  key={decade}
                  className="flex items-center justify-between text-xs"
                  data-testid={`overdue-ball-${ballIdx + 1}-decade-${decade}`}
                >
                  <DecadeChip label={label} />
                  <span className={`font-mono font-bold ${count === decadeCounts[0].count && count > 0 ? 'text-rose-600' : 'text-gray-600'}`}>
                    {count} time{count !== 1 ? 's' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Section B: full decade x ball matrix with row totals */}
      <h4 className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">All Balls &amp; Decades (with Totals)</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[520px]">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-2 text-[10px] uppercase text-gray-500 font-bold">Decade</th>
              {BALL_LABELS.map((label) => (
                <th key={label} className="text-center py-2 px-2 text-[10px] uppercase text-gray-500 font-bold">{label}</th>
              ))}
              <th className="text-center py-2 px-2 text-[10px] uppercase text-gray-700 font-black border-l border-gray-200">Total</th>
            </tr>
          </thead>
          <tbody>
            {byDecade.map(({ decade, label, counts, total }) => (
              <tr key={decade} className="border-b border-gray-50" data-testid={`overdue-decade-row-${decade}`}>
                <td className="py-2 px-2"><DecadeChip label={label} /></td>
                {counts.map((count, ballIdx) => (
                  <td
                    key={ballIdx}
                    className="text-center py-2 px-2 font-mono text-gray-700"
                    data-testid={`overdue-matrix-ball-${ballIdx + 1}-decade-${decade}`}
                  >
                    {count}
                  </td>
                ))}
                <td
                  className={`text-center py-2 px-2 font-mono font-black border-l border-gray-200 ${total === maxTotal && total > 0 ? 'text-rose-600' : 'text-gray-900'}`}
                  data-testid={`overdue-decade-total-${decade}`}
                >
                  {counts.join('+')} = {total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-500 italic mt-3">
        &quot;By Ball&quot; ranks each ball&apos;s decades by count (e.g. &quot;Ball 5 = 41-50 = 9 times&quot;). The matrix below shows every ball x decade combination plus a row Total (e.g. decade 1-10&apos;s row sums how many of the {overdueRows.length} record-drought types land in 1-10 across Ball 1 through Ball 5). Same {overdueRows.length} rows are highlighted in rose further down in the main table.
      </p>
    </div>
  );
}


/* ================================================================
 * Main page
 * ================================================================ */
export default function WindowTypes126() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/analysis/types-126`).then((res) => {
      if (alive) setData(res.data);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="window-types-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="types-back-btn">
            <ArrowLeft size={20} />
          </button>
          <Shapes size={24} weight="bold" className="text-[#002FA7]" />
          <div>
            <h1 className="text-lg sm:text-xl font-black text-gray-900">W67: Types — 126 Decade-Bucket Patterns</h1>
            <p className="text-xs text-gray-500">Live classification of every draw into 1 of 126 possible decade-shape types</p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center py-20" data-testid="types-loading"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <div className="space-y-6">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs text-emerald-800 font-semibold flex items-center gap-2" data-testid="types-live-banner">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live — recomputed from all {data.total_draws.toLocaleString()} draws on this page load (through draw #{data.latest_draw}, {data.latest_date}). Updates automatically after every new draw — nothing to refresh or regenerate.
            </div>

            <WindowNarrative windowId="TYPES" intro={buildIntro(data)} observations={buildObservations(data.rows)} keyFindings={buildKeyFindings(data.rows)} />

            <PerBallOverdueDecades rows={data.rows} />

            <TypesTable rows={data.rows} />
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500" data-testid="types-error">Could not load Types data.</div>
        )}
      </main>
    </div>
  );
}
